<script setup lang="ts">
/**
 * A reported figure's fields.
 *
 * The two periods sit side by side because they are read side by side, and
 * because putting the comparative somewhere else is how a report ends up with
 * this year's figure against last year's label.
 */
import { useI18n } from 'vue-i18n'

import type { DatapointSection } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: DatapointSection }>()
const emit = defineEmits<{ change: [section: DatapointSection] }>()

function set(changes: Partial<DatapointSection>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.datapoint.label')">
      <input
        class="b-input"
        :value="section.label"
        :placeholder="t('inspector.datapoint.labelPlaceholder')"
        @input="set({ label: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <div class="pair">
      <InspectorField :label="t('inspector.datapoint.value')">
        <input
          class="b-input"
          :value="section.value"
          @input="set({ value: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField
        :label="t('inspector.datapoint.unit')"
        :hint="t('inspector.datapoint.unitHint')"
      >
        <input
          class="b-input"
          :value="section.unit"
          :placeholder="t('inspector.datapoint.unitPlaceholder')"
          @input="set({ unit: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>
    </div>

    <InspectorField :label="t('inspector.datapoint.period')">
      <input
        class="b-input"
        :value="section.period"
        :placeholder="t('inspector.datapoint.periodPlaceholder')"
        @input="set({ period: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <div class="pair">
      <InspectorField :label="t('inspector.datapoint.comparative')">
        <input
          class="b-input"
          :value="section.comparative"
          @input="set({ comparative: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.datapoint.comparativePeriod')">
        <input
          class="b-input"
          :value="section.comparativePeriod"
          :placeholder="t('inspector.datapoint.comparativePeriodPlaceholder')"
          @input="set({ comparativePeriod: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>
    </div>

    <InspectorField
      :label="t('inspector.datapoint.method')"
      :hint="t('inspector.datapoint.methodHint')"
    >
      <textarea
        class="b-textarea"
        :value="section.method"
        @input="set({ method: ($event.target as HTMLTextAreaElement).value })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

/* Two fields that are read together, edited together. */
.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4);
}
</style>
